import { Check, Globe, Mail, MessageCircle, Phone, Search, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { UserAvatar } from '../../components/UserAvatar';
import { usePersons } from '../persons/use-persons';

import {
  availableChannels,
  personDistinguishingLine,
  recipientRestrictionMessage,
} from './proactive-adapter';

import type { ChannelCard } from './proactive-adapter';
import type { RecipientStatus } from './types';
import type { PersonDetail, PersonListItem } from '../persons/types';
import type { ChannelKind } from '@lume/shared';

export interface SelectedPerson {
  id: string;
  displayName: string;
  subtitle: string;
}

interface Step1Props {
  selected: SelectedPerson | null;
  onSelectPerson: (p: SelectedPerson) => void;
  /** Detalhe da pessoa selecionada (para derivar os canais reais). */
  person: PersonDetail | undefined;
  personLoading: boolean;
  channel: ChannelKind | null;
  onSelectChannel: (wire: ChannelKind) => void;
  /**
   * R-G9 — estado de restrição do destinatário, na forma exata do wire. `undefined` = ainda não
   * sabemos (carregando ou falha); nesse caso NÃO avisamos nada, e a porta é que recusa —
   * fail-open no aviso, de propósito (o fail-closed vive no 422 do POST).
   */
  restriction: RecipientStatus | undefined;
}

// N2-17 (noturna 2, 05/Set/2026) — a linha mostra o que DISTINGUE a pessoa (canal +
// identificador mascarado + última conversa), não só «1 canal». Vide `personDistinguishingLine`.
function personSubtitle(p: PersonListItem): string {
  return personDistinguishingLine(p);
}

function ChannelIcon({ wire }: { wire: ChannelKind }): JSX.Element {
  if (wire === 'whatsapp-cloud-api') return <Phone size={14} />;
  if (wire === 'telegram') return <Send size={14} />;
  if (wire === 'webchat') return <Globe size={14} />;
  if (wire === 'instagram-dm') return <MessageCircle size={14} />;
  return <Mail size={14} />;
}

/**
 * Passo 1 — Pessoa & canal. Busca via `listPersons({q})` (reuso Tela 2). Com a
 * busca vazia, mostra a 1ª página de pessoas (dado real, rótulo honesto — sem
 * endpoint de "recentes"). Os cards de canal são filtrados às identidades REAIS
 * da pessoa (D7): canal sem identidade faria o POST falhar com 422 `no_identity`.
 *
 * N2-17 — a busca é SERVER-SIDE (`GET /v1/persons?q=`) e, desde este MR, o `q` casa também o
 * identificador de canal (`Identity.channelIdentifier`); não há filtro client-side aqui.
 */
export function Step1PessoaCanal({
  selected,
  onSelectPerson,
  person,
  personLoading,
  channel,
  onSelectChannel,
  restriction,
}: Step1Props): JSX.Element {
  const [input, setInput] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQ(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isLoading, isError } = usePersons(q === '' ? { limit: 8 } : { q, limit: 8 });
  const results = data?.data ?? [];

  const channels: ChannelCard[] = useMemo(
    () => availableChannels(person?.identities ?? []),
    [person],
  );

  return (
    <>
      <div className="coleta-section">
        <span className="coleta-section__label">Para quem você quer enviar</span>

        {selected !== null && (
          <div
            className="aux-row"
            style={{
              border: '1px solid var(--brand)',
              borderRadius: 'var(--radius-sm)',
              padding: 'var(--space-2) var(--space-3)',
              background: 'color-mix(in srgb, var(--brand-subtle) 25%, var(--bg-surface))',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flex: 1 }}>
              <UserAvatar name={selected.displayName} colorKey={selected.id} size="md" />
              <div>
                <div className="aux-row__title">{selected.displayName}</div>
                {selected.subtitle !== '' && (
                  <div className="aux-row__sub mono">{selected.subtitle}</div>
                )}
              </div>
            </div>
            <Check size={14} style={{ color: 'var(--brand)' }} />
          </div>
        )}

        {selected !== null && restriction?.restricted === true && (
          <div
            className="form-row__note"
            role="status"
            style={{ color: 'var(--danger)', marginTop: 'var(--space-2)' }}
          >
            {recipientRestrictionMessage(restriction.cause)} O envio será bloqueado.
          </div>
        )}

        <div className="form-row">
          <span className="form-row__label">Buscar pessoa</span>
          <div style={{ position: 'relative' }}>
            <Search
              size={13}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--fg-3)',
              }}
            />
            <input
              className="input"
              type="text"
              aria-label="Buscar pessoa"
              placeholder="Nome, CPF, telefone ou identificador do canal…"
              style={{ paddingLeft: 32, width: '100%' }}
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
        </div>

        <div className="muted mono" style={{ fontSize: 10, marginTop: 4 }}>
          {q === '' ? 'Pessoas' : 'Resultados'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {isLoading && (
            <div className="muted" style={{ fontSize: 12 }}>
              Carregando…
            </div>
          )}
          {isError && (
            <div className="muted" style={{ fontSize: 12 }}>
              Não foi possível buscar pessoas.
            </div>
          )}
          {!isLoading && !isError && results.length === 0 && (
            <div className="muted" style={{ fontSize: 12 }}>
              Nenhuma pessoa encontrada.
            </div>
          )}
          {results.map((p) => {
            const isSel = selected?.id === p.id;
            return (
              <button
                key={p.id}
                type="button"
                className="aux-row"
                style={{
                  cursor: 'pointer',
                  textAlign: 'left',
                  background: 'none',
                  border: '1px solid ' + (isSel ? 'var(--brand)' : 'var(--border-subtle)'),
                  borderRadius: 'var(--radius-sm)',
                  padding: 'var(--space-2) var(--space-3)',
                }}
                onClick={() =>
                  onSelectPerson({
                    id: p.id,
                    displayName: p.displayName,
                    subtitle: personSubtitle(p),
                  })
                }
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    flex: 1,
                  }}
                >
                  <UserAvatar name={p.displayName} colorKey={p.id} size="md" />
                  <div>
                    <div className="aux-row__title">{p.displayName}</div>
                    <div className="aux-row__sub mono">{personSubtitle(p)}</div>
                  </div>
                </div>
                {isSel && <Check size={14} style={{ color: 'var(--brand)' }} />}
              </button>
            );
          })}
        </div>
      </div>

      {selected !== null && (
        <div className="coleta-section">
          <span className="coleta-section__label">Canal de envio</span>
          {personLoading ? (
            <div className="muted" style={{ fontSize: 12 }}>
              Carregando canais…
            </div>
          ) : channels.length === 0 ? (
            <div className="form-row__note">
              Esta pessoa não tem identidade em nenhum canal endereçável. Sem um canal verificado o
              envio é bloqueado.
            </div>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${Math.min(channels.length, 3)}, 1fr)`,
                gap: 'var(--space-2)',
              }}
            >
              {channels.map((ch) => {
                const isSel = channel === ch.wire;
                return (
                  <button
                    key={ch.wire}
                    type="button"
                    className="aux-card"
                    style={{
                      padding: 'var(--space-3)',
                      cursor: 'pointer',
                      borderWidth: isSel ? '2px' : '1px',
                      borderColor: isSel ? 'var(--brand)' : 'var(--border-default)',
                      background: isSel
                        ? 'color-mix(in srgb, var(--brand-subtle) 25%, var(--bg-surface))'
                        : 'var(--bg-surface)',
                    }}
                    onClick={() => onSelectChannel(ch.wire)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      <ChannelIcon wire={ch.wire} />
                      <span className="aux-row__title">{ch.label}</span>
                    </div>
                    <span className="aux-row__sub mono" style={{ fontSize: 10 }}>
                      {ch.identifier}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}
